import { ChainId } from '@traderjoe-xyz/joepegs-sdk'
import {
  Collection,
  CollectionType,
  CollectionVerificationStatus,
  ItemDetail,
  ItemMetadata
} from 'types/nft'
import { parseAttributes } from 'utils/attributes'
import { DataApiNftMetadata, DataApiNftToken } from 'utils/avalancheDataApi'
import { convertChainIdToChain } from 'utils/chain'
import { toHttpUrl } from 'utils/ipfs'

const INDEXED = 'INDEXED'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const toOptionalString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null

const toOptionalUrl = (value: unknown): string | null =>
  typeof value === 'string' ? toHttpUrl(value) : null

/** Maps Data API metadata to item metadata. Returns null unless indexed. */
export const toItemMetadata = (
  metadata: DataApiNftMetadata | undefined
): ItemMetadata | null => {
  if (!metadata || metadata.indexStatus !== INDEXED) {
    return null
  }
  return {
    animationUrl: toHttpUrl(metadata.animationUri),
    attributes: parseAttributes(metadata.attributes),
    description: metadata.description ?? null,
    externalUrl: metadata.externalUrl ?? null,
    image: toHttpUrl(metadata.imageUri),
    name: metadata.name ?? null
  }
}

/** Maps the JSON served by a token URI to item metadata. */
export const toTokenUriMetadata = (json: unknown): ItemMetadata | null => {
  if (!isRecord(json)) {
    return null
  }
  return {
    animationUrl: toOptionalUrl(json.animation_url),
    attributes: parseAttributes(json.attributes),
    description: toOptionalString(json.description),
    externalUrl: toOptionalString(json.external_url),
    image: toOptionalUrl(json.image),
    name: toOptionalString(json.name)
  }
}

const decodeBase64Utf8 = (base64: string): string => {
  const binary = atob(base64)
  const bytes = Array.from(
    binary,
    (char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`
  )
  return decodeURIComponent(bytes.join(''))
}

const DATA_URI_PATTERN = /^data:application\/json(;[^,]*)?,([\s\S]*)$/

/**
 * Decodes an inline `data:application/json` token URI.
 * Returns undefined when the URI is not inline JSON.
 */
export const readInlineTokenUri = (tokenUri: string): unknown => {
  const match = DATA_URI_PATTERN.exec(tokenUri)
  if (!match) {
    return undefined
  }
  const [, params = '', payload] = match
  const text = params.split(';').includes('base64')
    ? decodeBase64Utf8(payload)
    : decodeURIComponent(payload)
  return JSON.parse(text)
}

/** ERC-1155 rows with a zero balance are returned by the API and not owned. */
export const isOwnedBalance = (token: DataApiNftToken): boolean =>
  token.ercType !== 'ERC-1155' || Number(token.balance ?? 0) > 0

const toCollectionType = (token: DataApiNftToken): CollectionType =>
  token.ercType === 'ERC-1155' ? CollectionType.ERC1155 : CollectionType.ERC721

/**
 * Maps a Data API token row to an item. Collection fields come from the
 * registry entry; the token row's name and symbol are the fallback.
 * The owner is the given address, else the row's ownerAddress.
 */
export const toItem = (
  token: DataApiNftToken,
  chainId: ChainId,
  collection: Collection | undefined,
  ownerAddress?: string
): ItemDetail => {
  const chain = convertChainIdToChain(chainId)
  const address = token.address.toLowerCase()
  const ownerId = ownerAddress ?? token.ownerAddress
  const quantity = token.ercType === 'ERC-1155' ? Number(token.balance ?? 0) : 1
  return {
    chain,
    chainId,
    collection: address,
    collectionDescription: collection?.description ?? null,
    collectionName: collection?.name ?? token.name ?? null,
    collectionNumItems: collection?.numItems,
    collectionPfpUrl: collection?.pfpUrl ?? null,
    collectionSlug: collection?.slugName ?? null,
    collectionSymbol: collection?.symbol ?? token.symbol ?? null,
    collectionType: toCollectionType(token),
    id: `${chain}:${address}:${token.tokenId}`,
    metadata: toItemMetadata(token.metadata),
    owner: ownerId ? { ownerId: ownerId.toLowerCase(), quantity } : null,
    tokenId: token.tokenId,
    tokenUri: token.tokenUri || null,
    verified: collection?.verified ?? CollectionVerificationStatus.Unverified
  }
}
